import { AxiosResponse } from 'axios';

/**
 * Force saving a file to local disk of user by manually creating a link and downloading it
 * @param blob represents the data coming from the server as a Blob object
 * @param path represents the full path where it should be added including the file name ex: 'file.png' or './path/to/file.png'
 * @param options represents the Blob config option objects
 */
export const saveFile = (blob: Blob, path: string, options?: Record<string, any>): void => {
  const url = window.URL.createObjectURL(new Blob([blob], options));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', path);
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(link.href);
};

export const saveAttachment = (blob: Blob, path: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', path || 'download');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const FILENAME_REGEX = /filename=(?:(?:"|'|\\"|\\')([^\\"]+)|([^"\s]+))/g;

export const getFileNameFromAxiosHeader = (headers: AxiosResponse['headers']): string | undefined => {
  const contentDispositionHeader = headers['content-disposition'];

  if (typeof contentDispositionHeader !== 'string') {
    return;
  }
  const matches = FILENAME_REGEX.exec(contentDispositionHeader) ?? [];

  if (matches?.length < 2) {
    return;
  }

  return matches[1] ?? matches[2] ?? undefined;
};

type FileRequestType<T> = { file: File; otherParams?: T };
export const buildFormData = <T extends object>(data: FileRequestType<T> | FileRequestType<T>[]): FormData => {
  const formData = new FormData();

  const list = Array.isArray(data) ? [...data] : [data];

  list.forEach((item) => {
    formData.append('file', item.file);

    if (item.otherParams) {
      Object.entries(item.otherParams).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
  });

  return formData;
};

export const formatFileSize = (bytes: number): string => {
  const kb = bytes / 1024;
  const mb = bytes / (1024 * 1024);

  if (bytes < 1024 * 1024) {
    return `${kb.toFixed(0)} KB`;
  }

  return `${mb.toFixed(2)} MB`;
};

type Primitive = string | number | boolean | null | undefined;
type FormDataValue = Primitive | Primitive[] | File | File[];

export const buildRequestFormData = (payload: Record<string, FormDataValue>): FormData => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    // Arrays
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          formData.append(key, item instanceof File ? item : (item as any));
        }
      });
      return;
    }

    // Single values
    formData.append(key, value instanceof File ? value : (value as any));
  });

  return formData;
};

export const downloadFileFromBase64 = (base64String: string, fileName: string, contentType: string) => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  link.click();

  URL.revokeObjectURL(url);
};
